import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import axios, { AxiosError } from 'axios';
import { merge, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UNKNOWN_STATE_LABEL } from 'src/app/shared/payee-cases/payee-cases.component';
import { PayeeCasesService } from 'src/app/shared/payee-cases/payee-cases.service';
import { StateOption } from 'src/app/shared/payee-cases/types';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'payee-case-state-filter',
  templateUrl: './case-state-filter.component.html',
  styleUrls: ['./case-state-filter.component.scss']
})
export class CaseStateFilterComponent implements OnInit, OnDestroy {
  private readonly destroy = new Subject<void>();

  dropdownOpen = false;

  stateControl = new FormControl(null);
  stateOptions: StateOption[] = [];

  get state() { return this.payeeCasesService.state.value; }

  constructor(
    private payeeCasesService: PayeeCasesService,
    private translate: TranslateService,
    private snackbar: MatSnackBar,
  ) { }

  ngOnInit(): void {
    merge(
      this.translate.onLangChange,
      this.payeeCasesService.cases,
    )
      .pipe(takeUntil(this.destroy))
      .subscribe(async () => {
        await this.setStates();
      });
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }

  displayWith(state?: StateOption): string {
    return state?.label ?? '';
  }

  applyFilter(): void {
    this.payeeCasesService.state.next(this.stateControl.value);
    this.dropdownOpen = false;
  }

  clearFilter(): void {
    this.payeeCasesService.state.next(null);
    this.dropdownOpen = false;
  }

  private async setStates(): Promise<void> {
    const url = `${environment.baseUrl}/api/case-states`;

    try {
      const result = await axios.get<{ states: StateOption[]; }>(url, {
        params: this.payeeCasesService.getFilters(),
      });

      this.stateOptions = result.data.states;

      if (this.state?.label === UNKNOWN_STATE_LABEL) {
        const state = this.stateOptions.find(v => v.id === this.state?.id);
        if (state) {
          this.stateControl.patchValue(state ?? null);
          this.applyFilter();
        }
      }
    } catch (error: AxiosError | unknown) {
      if (error instanceof AxiosError) {
        console.error('Network error while getting states', error.response?.data);
      } else {
        console.error('Unknown error while getting states', error);
      }

      this.snackbar.open(
        this.translate.instant('CASES_TABLE.ERROR_MESSAGES.ERROR_WHILE_LOADING_STATES'),
        'OK',
        { duration: 5000 },
      );
    }
  }
}
